import UserPermissionTypes from '@constants/permissions';

/**
 * Custom-role permission UI: visibility, merges, and display order.
 *
 * Visibility:
 * - shown in create/view checklists by default
 * - explicitly hidden permissions are omitted; only branch_view is
 *   auto-included when assignable
 * - payment_order_edit → hide; AND of split payment actions (handled separately)
 */

export type PermissionUiMergeGroup = {
  /** Stable id used as the checkbox slug in the UI. */
  id: string;
  label: string;
  /** All API permission keys toggled together. First is the preferred display slug. */
  slugs: string[];
};

export const PERMISSION_UI_MERGE_GROUPS: PermissionUiMergeGroup[] = [
  {
    id: UserPermissionTypes.DASHBOARD_VIEW,
    label: 'View branch dashboard',
    slugs: [
      UserPermissionTypes.DASHBOARD_VIEW,
      UserPermissionTypes.BRANCH_SUMMARY_VIEW
    ]
  },
  {
    id: UserPermissionTypes.PAYMENT_ORDER_GENERATE_CUSTOM_REPORT,
    label: 'Download collections report',
    slugs: [
      UserPermissionTypes.PAYMENT_ORDER_GENERATE_CUSTOM_REPORT,
      UserPermissionTypes.PAYMENT_ORDER_GENERATE_REPORT
    ]
  },
  {
    id: UserPermissionTypes.EVENT_ATTENDEE_LIST,
    label: 'View event attendees',
    slugs: [
      UserPermissionTypes.EVENT_ATTENDEE_LIST,
      UserPermissionTypes.EVENT_ATTENDEE_VIEW
    ]
  },
  {
    id: UserPermissionTypes.EVENT_EDIT,
    label: 'Edit event',
    slugs: [
      UserPermissionTypes.EVENT_EDIT,
      UserPermissionTypes.EVENT_STATUS_CHANGE,
      UserPermissionTypes.EVENT_TICKET_STATUS_CHANGE
    ]
  },
  {
    id: UserPermissionTypes.EVENT_QR_ATTENDEE_VIEW,
    label: 'Check in attendee',
    slugs: [
      UserPermissionTypes.EVENT_QR_ATTENDEE_VIEW,
      UserPermissionTypes.EVENT_BOOKING_ID_READ,
      UserPermissionTypes.EVENT_QR_ATTENDEE_CHECK_IN
    ]
  },
  {
    id: UserPermissionTypes.LEAD_MANAGEMENT_CREATE,
    label: 'Create lead',
    slugs: [
      UserPermissionTypes.LEAD_MANAGEMENT_CREATE,
      UserPermissionTypes.LEAD_BULK_UPLOAD
    ]
  },
  {
    id: UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW,
    label: 'View lead settings',
    slugs: [
      UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW,
      UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_EDIT
    ]
  },
  {
    id: UserPermissionTypes.FORM_VIEW,
    label: 'View forms',
    slugs: [
      UserPermissionTypes.FORM_VIEW,
      UserPermissionTypes.FORM_SUBMISSION_VIEW
    ]
  },
  {
    id: UserPermissionTypes.FORM_EDIT,
    label: 'Edit form',
    slugs: [
      UserPermissionTypes.FORM_EDIT,
      UserPermissionTypes.FORM_STATUS_CHANGE
    ]
  }
];

/** Explicitly hidden from custom-role UI (except special cases). */
const HIDDEN_FROM_CUSTOM_ROLE_UI = new Set<string>([
  UserPermissionTypes.BRANCH_VIEW,
  UserPermissionTypes.PAYMENT_ORDER_EDIT,
  UserPermissionTypes.BRANCH_DISABLE,
  UserPermissionTypes.KYC_DOCUMENT_STATUS_CHANGE,
  UserPermissionTypes.KYC_DOCUMENT_VIEW,
  UserPermissionTypes.SUBSCRIPTION_VIEW,
  UserPermissionTypes.SUBSCRIPTION_EDIT,
  UserPermissionTypes.ORGANIZATION_STATUS_CHANGE,
  UserPermissionTypes.CUSTOMER_DETAILS_EDIT,
  UserPermissionTypes.CUSTOMER_DETAILS_VIEW,
  UserPermissionTypes.ORGANIZATION_LIST_VIEW,
  UserPermissionTypes.ORGANIZATION_DETAILS_VIEW,
  UserPermissionTypes.CUSTOMER_LIST_VIEW,
  UserPermissionTypes.ESIGN_CREATE,
  UserPermissionTypes.ESIGN_VIEW,
  UserPermissionTypes.OWNER_VIEW,
  UserPermissionTypes.SEND_ADMIN_INVITES,
  UserPermissionTypes.DELETE_ADMIN_INVITES,
  UserPermissionTypes.VIEW_ADMIN_INVITES,
  UserPermissionTypes.SUCCESS_ALERT_EDIT,
  UserPermissionTypes.SUCCESS_ALERT_VIEW,
  UserPermissionTypes.CSV_TEMPLATE_PROCESSOR,
  UserPermissionTypes.EVENT_BRANCH_VIEW
]);

// This creates a Set containing all "secondary" permission slugs
// (i.e., all slugs except the first one) from each group in PERMISSION_UI_MERGE_GROUPS.
// These secondary slugs are typically hidden from the UI,
// and are used to determine which permissions should not be shown individually
// if they are represented as part of a merge group.
const MERGE_SECONDARY_SLUGS = new Set(
  PERMISSION_UI_MERGE_GROUPS.flatMap((group) => group.slugs.slice(1))
);

const MERGE_BY_SLUG = new Map<string, PermissionUiMergeGroup>();
PERMISSION_UI_MERGE_GROUPS.forEach((group) => {
  group.slugs.forEach((slug) => MERGE_BY_SLUG.set(slug, group));
});

export const getPermissionMergeGroup = (
  slug: string
): PermissionUiMergeGroup | undefined => MERGE_BY_SLUG.get(slug);

/** Hidden / undecided permissions (except finance) — omit from UI and payload. */
export const isOmittedCustomRolePermission = (slug: string): boolean =>
  HIDDEN_FROM_CUSTOM_ROLE_UI.has(slug);

/**
 * Eligible for the create/view checklist before merge collapse.
 * Includes merge secondaries so incomplete groups can still surface.
 */
export const isPermissionEligibleForCustomRoleUi = (slug: string): boolean =>
  !isOmittedCustomRolePermission(slug);

export const isPermissionVisibleInCustomRoleUi = (slug: string): boolean => {
  if (!isPermissionEligibleForCustomRoleUi(slug)) return false;
  if (MERGE_SECONDARY_SLUGS.has(slug)) return false;
  return true;
};

/** Always include in create payload when assignable; never show in UI. */
export const isAutoIncludedHiddenPermission = (slug: string): boolean =>
  slug === UserPermissionTypes.BRANCH_VIEW;

export const stripOmittedCustomRolePermissions = (
  permissions: string[],
  preservedSlugs: ReadonlySet<string> = new Set()
): string[] =>
  permissions.filter(
    (slug) =>
      preservedSlugs.has(slug) ||
      isAutoIncludedHiddenPermission(slug) ||
      !isOmittedCustomRolePermission(slug)
  );

export const expandPermissionUiSelection = (
  slug: string,
  assignablePermissions: string[]
): string[] => {
  const group = getPermissionMergeGroup(slug);
  const candidates = group?.slugs ?? [slug];
  return candidates.filter((item) => assignablePermissions.includes(item));
};

export const collapsePermissionsForUi = (slugs: string[]): string[] => {
  const slugSet = new Set(slugs);
  const result: string[] = [];
  // seenMergeIds tracks which merge groups have already been processed,
  // ensuring that each group (represented by its primary/representative ID)
  // is only included once in the returned result, even if multiple
  // slugs from that group appear in the input.
  const seenMergeIds = new Set<string>();

  slugs.forEach((slug) => {
    const group = getPermissionMergeGroup(slug);
    if (group) {
      // Only push each group's representative (primary) slug once.
      if (seenMergeIds.has(group.id)) return;
      seenMergeIds.add(group.id);
      if (group.slugs.some((member) => slugSet.has(member))) {
        result.push(group.id);
      }
      return;
    }

    if (!isPermissionVisibleInCustomRoleUi(slug)) return;
    result.push(slug);
  });

  return result;
};

export const getMergeLabel = (slug: string): string | undefined =>
  getPermissionMergeGroup(slug)?.label;

export const areMergePermissionsSelected = (
  slug: string,
  selectedPermissions: string[],
  assignablePermissions: string[]
): boolean => {
  const expanded = expandPermissionUiSelection(slug, assignablePermissions);
  if (expanded.length === 0) return selectedPermissions.includes(slug);
  return expanded.every((item) => selectedPermissions.includes(item));
};

/**
 * Custom-role permission display order.
 * Merge groups use their primary UI slug only.
 */
export const PERMISSION_DISPLAY_ORDER: string[] = [
  UserPermissionTypes.BRANCH_EDIT,
  UserPermissionTypes.ACCOUNT_DETAILS_VIEW,
  UserPermissionTypes.ACCOUNT_DETAILS_SENSITIVE_DATA_VIEW,
  UserPermissionTypes.BRANCH_CREATE,
  UserPermissionTypes.BRANCH_MEMBER_VIEW,
  UserPermissionTypes.BRANCH_MEMBER_CREATE,
  UserPermissionTypes.BRANCH_MEMBER_EDIT,
  UserPermissionTypes.BRANCH_MEMBER_DELETE,
  UserPermissionTypes.GROUP_VIEW,
  UserPermissionTypes.GROUP_PAYMENT_SUMMARY_VIEW,
  UserPermissionTypes.GROUP_CREATE,
  UserPermissionTypes.GROUP_EDIT,
  UserPermissionTypes.GROUP_DISABLE,
  UserPermissionTypes.GROUP_MEMBER_VIEW,
  UserPermissionTypes.GROUP_MEMBER_CREATE,
  UserPermissionTypes.GROUP_MEMBER_EDIT,
  UserPermissionTypes.GROUP_ATTENDANCE_VIEW,
  UserPermissionTypes.GROUP_ATTENDANCE_EDIT,
  UserPermissionTypes.GROUP_ATTENDANCE_REPORT_DOWNLOAD,
  UserPermissionTypes.PAYMENT_ORDER_GENERATE_GROUP_CUSTOM_REPORT,
  UserPermissionTypes.PAYMENT_ORDER_VIEW,
  UserPermissionTypes.PAYMENT_ORDER_CREATE,
  UserPermissionTypes.PAYMENT_ORDER_MARK_PAID,
  UserPermissionTypes.PAYMENT_ORDER_SPLIT_ENABLE,
  UserPermissionTypes.PAYMENT_ORDER_DISCOUNT_CREATE,
  UserPermissionTypes.PAYMENT_ORDER_DISABLE,
  UserPermissionTypes.PAYMENT_ORDER_CLOSE,
  UserPermissionTypes.PAYMENT_ORDER_GENERATE_RECEIPT,
  UserPermissionTypes.PAYMENT_ORDER_SEND_REMINDER,
  UserPermissionTypes.PAYMENT_ORDER_GENERATE_GST_REPORT,
  UserPermissionTypes.PAYMENT_ORDER_GENERATE_CUSTOM_REPORT,
  UserPermissionTypes.EXPENSE_VIEW,
  UserPermissionTypes.EXPENSE_CREATE,
  UserPermissionTypes.EXPENSE_STATUS_CHANGE,
  UserPermissionTypes.EXPENSE_DELETE,
  UserPermissionTypes.ORGANISATION_SETTINGS_EDIT,
  UserPermissionTypes.DASHBOARD_VIEW,
  UserPermissionTypes.ORG_DASHBOARD_VIEW,
  UserPermissionTypes.EVENT_VIEW,
  UserPermissionTypes.EVENT_ATTENDEE_LIST,
  UserPermissionTypes.EVENT_CREATE,
  UserPermissionTypes.EVENT_EDIT,
  UserPermissionTypes.EVENT_QR_ATTENDEE_VIEW,
  UserPermissionTypes.LEAD_MANAGEMENT_VIEW,
  UserPermissionTypes.LEAD_MANAGEMENT_CREATE,
  UserPermissionTypes.LEAD_MANAGEMENT_EDIT,
  UserPermissionTypes.LEAD_REPORT_GENERATE,
  UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW,
  UserPermissionTypes.FORM_VIEW,
  UserPermissionTypes.FORM_CREATE,
  UserPermissionTypes.FORM_EDIT,
  UserPermissionTypes.FORM_EXPORT,
  UserPermissionTypes.INTEGRATION_EDIT,
  UserPermissionTypes.ORG_ADMIN_VIEW,
  UserPermissionTypes.ORG_ADMIN_CREATE,
  UserPermissionTypes.ORG_ADMIN_EDIT,
  UserPermissionTypes.BRANCH_ADMIN_VIEW,
  UserPermissionTypes.BRANCH_ADMIN_CREATE,
  UserPermissionTypes.BRANCH_ADMIN_EDIT,
  UserPermissionTypes.GROUP_ADMIN_VIEW,
  UserPermissionTypes.GROUP_ADMIN_CREATE,
  UserPermissionTypes.GROUP_ADMIN_EDIT,
  UserPermissionTypes.FINANCE_ADMIN_VIEW,
  UserPermissionTypes.FINANCE_ADMIN_CREATE,
  UserPermissionTypes.FINANCE_ADMIN_EDIT,
  UserPermissionTypes.PAYMENT_MANAGER_VIEW,
  UserPermissionTypes.PAYMENT_MANAGER_CREATE,
  UserPermissionTypes.PAYMENT_MANAGER_EDIT,
  UserPermissionTypes.EVENT_MANAGER_VIEW,
  UserPermissionTypes.EVENT_MANAGER_CREATE,
  UserPermissionTypes.EVENT_MANAGER_EDIT,
  UserPermissionTypes.EVENT_VOLUNTEER_VIEW,
  UserPermissionTypes.EVENT_VOLUNTEER_CREATE,
  UserPermissionTypes.EVENT_VOLUNTEER_EDIT,
  UserPermissionTypes.SALES_MANAGER_VIEW,
  UserPermissionTypes.SALES_MANAGER_CREATE,
  UserPermissionTypes.SALES_MANAGER_EDIT,
  UserPermissionTypes.SALES_AGENT_VIEW,
  UserPermissionTypes.SALES_AGENT_CREATE,
  UserPermissionTypes.SALES_AGENT_EDIT,
  UserPermissionTypes.ATTENDANCE_MANAGER_VIEW,
  UserPermissionTypes.ATTENDANCE_MANAGER_CREATE,
  UserPermissionTypes.ATTENDANCE_MANAGER_EDIT
];

/** Module section order for custom-role permission lists. */
export const PERMISSION_MODULE_UI_ORDER: string[] = [
  'Branches',
  'Members',
  'Groups',
  'Payments',
  'Reports',
  'Expense tracker',
  'Organisation Settings',
  'Dashboard',
  'Events',
  'Lead management',
  'Forms',
  'Integrations',
  'Team management',
  'Other'
];

const PERMISSION_DISPLAY_ORDER_INDEX = new Map(
  PERMISSION_DISPLAY_ORDER.map((slug, index) => [slug, index])
);

const MODULE_UI_ORDER_INDEX = new Map(
  PERMISSION_MODULE_UI_ORDER.map((module, index) => [module, index])
);

export const getPermissionUiOrder = (slug: string): number =>
  PERMISSION_DISPLAY_ORDER_INDEX.get(slug) ?? Number.MAX_SAFE_INTEGER;

export const getModuleUiOrder = (module: string): number =>
  MODULE_UI_ORDER_INDEX.get(module) ?? Number.MAX_SAFE_INTEGER;

export const sortPermissionsByUiOrder = (slugs: string[]): string[] =>
  [...slugs].sort(
    (left, right) => getPermissionUiOrder(left) - getPermissionUiOrder(right)
  );
